/**
 * New console — reference lists, read once.
 *
 * The master categories, the roles, the alert types, the timezones, the states and the
 * clinic list change a few times a year, yet every screen that needed one fetched it on
 * every mount: opening Leads, then a lead, then Triage was three identical requests for
 * the reasons list. Each list is fetched once per session here, shared by everything
 * that asks, and asked for again only after `TTL` or when a screen knows it changed
 * (a clinic added or switched off — `forgetClinics`).
 *
 * Callers get the same response object the service would have given, so nothing about
 * how a screen reads it changes. Treat what comes back as read-only: it is the copy
 * every other screen will read too. A failed response is not remembered, so a screen
 * that retries gets a real second attempt.
 */
import { getMasterCategoryList } from '@services/calls';
import { getUserRolesListApi } from '@services/users';
import { getAlertTypesApi } from '@services/notificationSettings';
import { getTimezoneListApi } from '@services/timezone';
import { getStateListApi } from '@services/state';
import { getClinicsApi } from '@services/clinics';

const TTL = 10 * 60 * 1000;
const store = new Map(); // key -> { at, promise }

export const cached = (key, fetcher, { fresh = false, ttl = TTL } = {}) => {
  const hit = store.get(key);
  if (hit && !fresh && Date.now() - hit.at < ttl) return hit.promise;
  const promise = fetcher().then((res) => {
    if (!res || !res.isOk) store.delete(key);
    return res;
  });
  store.set(key, { at: Date.now(), promise });
  return promise;
};

export const forget = (key) => store.delete(key);

export const masterCategories = () => cached('master-categories', getMasterCategoryList);
export const userRoles = () => cached('user-roles', getUserRolesListApi);
export const alertTypes = () => cached('alert-types', getAlertTypesApi);
export const timezones = () => cached('timezones', getTimezoneListApi);
export const states = () => cached('states', getStateListApi);

/* Every clinic in one page — the topbar picker, the notification filter, the users form
   and the API keys overview all read this same list. `activeOnly` is what the service
   takes; the two answers are cached apart. */
export const clinicList = (activeOnly = false, opts) => cached(
  `clinics:${activeOnly ? 'active' : 'all'}`,
  () => getClinicsApi(1, 1000, null, !!activeOnly),
  opts
);

/** After a clinic is added, renamed or switched on or off. */
export const forgetClinics = () => { forget('clinics:active'); forget('clinics:all'); };
